import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { getDashboardStats, getHistorialStats, resetAllMuestras } from '../../services/muestrasApi';
import { toast } from 'sonner';
import {
  Layers,
  Grid3X3,
  Scissors,
  Shirt,
  Palette,
  Tag,
  Ruler,
  Clock,
  User,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const MuestrasDashboard = () => {
  const { user } = useApp();
  const [stats, setStats] = useState(null);
  const [historial, setHistorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, historialData] = await Promise.all([
        getDashboardStats(),
        getHistorialStats()
      ]);
      setStats(statsData);
      setHistorial(historialData);
    } catch (error) {
      toast.error('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAll = async () => {
    setResetting(true);
    try {
      const result = await resetAllMuestras();
      toast.success(`Módulo reiniciado. ${result.deleted_files || 0} archivos eliminados de la nube.`);
      setShowResetConfirm(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Error al reiniciar el módulo');
    } finally {
      setResetting(false);
    }
  };

  const mainCards = [
    { label: 'Muestras Base', value: stats?.total_muestras || 0, icon: Layers, color: 'bg-blue-500' },
    { label: 'Bases', value: stats?.total_bases || 0, icon: Grid3X3, color: 'bg-green-500' },
    { label: 'Tizados', value: stats?.total_tizados || 0, icon: Scissors, color: 'bg-purple-500' },
    { label: 'Telas Registradas', value: stats?.total_telas || 0, icon: Shirt, color: 'bg-orange-500' },
  ];

  const moduleCards = [
    { label: 'Marcas', value: stats?.total_marcas || 0, icon: Palette, color: 'text-pink-500 bg-pink-50' },
    { label: 'Tipos Producto', value: stats?.total_tipos || 0, icon: Tag, color: 'text-teal-500 bg-teal-50' },
    { label: 'Entalles', value: stats?.total_entalles || 0, icon: Ruler, color: 'text-indigo-500 bg-indigo-50' },
    { label: 'Telas', value: stats?.total_telas || 0, icon: Shirt, color: 'text-cyan-500 bg-cyan-50' },
    { label: 'Muestras Base', value: stats?.total_muestras || 0, icon: Layers, color: 'text-blue-500 bg-blue-50' },
    { label: 'Bases', value: stats?.total_bases || 0, icon: Grid3X3, color: 'text-green-500 bg-green-50' },
    { label: 'Tizados', value: stats?.total_tizados || 0, icon: Scissors, color: 'text-purple-500 bg-purple-50' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <h1 className="text-2xl font-bold">¡Hola, {user?.nombre?.split(' ')[0]}!</h1>
          <p className="text-blue-100 mt-1">Bienvenido al Sistema de Gestión de Muestras</p>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {mainCards.map((card, idx) => (
          <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{card.label}</p>
                <p className="text-3xl font-bold text-gray-800">{card.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center`}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Modules Grid */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Módulos del Sistema</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {moduleCards.map((card, idx) => (
              <div key={idx} className={`${card.color} rounded-xl p-4 text-center`}>
                <card.icon className="w-8 h-8 mx-auto mb-2" />
                <p className="text-2xl font-bold">{card.value}</p>
                <p className="text-xs mt-1 opacity-75">{card.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            Actividad Reciente
          </h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {historial?.recientes?.length > 0 ? (
              historial.recientes.slice(0, 8).map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    item.accion === 'crear' ? 'bg-green-100 text-green-600' :
                    item.accion === 'editar' ? 'bg-blue-100 text-blue-600' :
                    item.accion === 'eliminar' ? 'bg-red-100 text-red-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    <User className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">
                      {item.descripcion || `${item.accion} en ${item.tabla}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.username}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">No hay actividad reciente</p>
            )}
          </div>
        </div>
      </div>

      {/* Admin: Botón de Reset */}
      {user?.rol === 'admin' && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">Zona de Peligro</h3>
              <p className="text-sm text-red-600 mt-1">
                Reiniciar el módulo eliminará TODOS los datos: muestras, bases, fichas, tizados, telas, marcas, etc. 
                También se eliminarán los archivos de la nube. Esta acción NO se puede deshacer.
              </p>
              <button
                onClick={() => setShowResetConfirm(true)}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Reiniciar Módulo Completo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">¿Estás seguro?</h3>
                <p className="text-sm text-gray-500">Esta acción es irreversible</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Se eliminarán permanentemente:
            </p>
            <ul className="text-sm text-gray-600 mb-6 space-y-1">
              <li>• Todas las muestras base y bases</li>
              <li>• Todas las fichas y tizados</li>
              <li>• Todas las telas, marcas, tipos y entalles</li>
              <li>• Todos los archivos en Cloudflare R2</li>
            </ul>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                disabled={resetting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleResetAll}
                disabled={resetting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                {resetting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Sí, Eliminar Todo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasDashboard;
